import { useEffect, useRef, useState } from "react";
import { BanknoteArrowUp, ExternalLink } from "lucide-react";
import { tipEntries } from "@/lib/tips";

/**
 * Small tip icon that opens a popover listing a user's tip links.
 * Props: tipLinks (map), name (whose tips), size ("sm"|"md").
 * Renders nothing if there are no usable links.
 */
export default function TipButton({ tipLinks, name = "", size = "sm" }) {
    const entries = tipEntries(tipLinks);
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);
    if (!entries.length) return null;
    const handle = name ? `${String(name).replace(/^@/, "")}` : "this user";
    const dim = size === "md" ? "h-7 w-7" : "h-6 w-6";
    return (
        <span className="relative inline-flex" ref={ref}>
            <button
                type="button"
                data-testid="tip-button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((v) => !v); }}
                title={`Donate / tip to ${handle}`}
                aria-label={`Donate or tip to ${handle}`}
                className={`${dim} inline-flex items-center justify-center rounded-full border bg-[#141414] border-[#2a2a2a] text-[#a0a0a0] hover:border-[#ff4500] hover:text-[#ff4500] transition-colors`}
            >
                <BanknoteArrowUp className="h-3.5 w-3.5" />
            </button>
            {open && (
                <div data-testid="tip-popover" className="absolute z-50 top-full right-0 mt-2 w-52 bg-[#141414] border border-[#2a2a2a] shadow-2xl">
                    <div className="px-3 py-2 border-b border-[#2a2a2a] text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">
                        Donate / tip to {handle}
                    </div>
                    {entries.map((en) => (
                        <a
                            key={en.label}
                            href={en.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-between px-3 py-2 text-sm text-white hover:bg-[#0f0f0f] hover:text-[#22c55e] transition-colors"
                        >
                            {en.label} <ExternalLink className="h-3 w-3" />
                        </a>
                    ))}
                </div>
            )}
        </span>
    );
}
